// ============================================================
//  ANALYTICS CONFIGURATION
//  ----------------------------------------------------------
//  This is the ONE FILE you edit to wire up GA4, Microsoft
//  Clarity, and Google Search Console verification.
//
//  After editing the values below, redeploy the site.
// ============================================================

export const ANALYTICS_CONFIG = {
  // ─── Google Analytics 4 ────────────────────────────────────
  // Paste your GA4 Measurement ID below (format: "G-XXXXXXXXXX").
  // Where to find it:  Google Analytics → Admin → Data Streams
  //                    → Web → "Measurement ID"
  // Leave as "" to disable Google Analytics.
  GA4_MEASUREMENT_ID: "G-FVPRX9F8QL",

  // ─── Microsoft Clarity ─────────────────────────────────────
  // Paste your Clarity Project ID below (10-character string).
  // Where to find it:  clarity.microsoft.com → Settings →
  //                    Setup → "Project ID"
  // Leave as "" to disable Microsoft Clarity.
  CLARITY_PROJECT_ID: "x7q1zvth5k",

  // ─── Google Search Console (optional) ──────────────────────
  // If you choose the "HTML meta tag" verification method in
  // Search Console, paste the `content` value here (NOT the
  // whole meta tag — just the token).
  // Leave as "" if you verified via DNS or HTML file upload.
  GOOGLE_SITE_VERIFICATION: "",
};

// ── Internal helpers — do not edit below this line ─────────

const isBrowser = typeof window !== "undefined";

const isExcludedPath = (path) =>
  path.startsWith("/admin") || path === "/__healthcheck";

/** Initialize GA4 (loads gtag.js, sets up dataLayer, registers initial pageview). */
export function initGA4() {
  if (!isBrowser) return;
  const id = ANALYTICS_CONFIG.GA4_MEASUREMENT_ID;
  if (!id || document.querySelector(`script[data-ga4="${id}"]`)) return;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  s.setAttribute("data-ga4", id);
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", id, { send_page_view: false });
}

/** Initialize Microsoft Clarity. */
export function initClarity() {
  if (!isBrowser) return;
  const id = ANALYTICS_CONFIG.CLARITY_PROJECT_ID;
  if (!id || window.clarity) return;

  // Standard Clarity bootstrap snippet
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", id);
}

/** Track a page view (called automatically on route change). */
export function trackPageview(pathname, search = "") {
  if (!isBrowser || isExcludedPath(pathname)) return;
  const id = ANALYTICS_CONFIG.GA4_MEASUREMENT_ID;
  const fullPath = `${pathname}${search}`;
  if (id && window.gtag) {
    window.gtag("event", "page_view", {
      page_path: fullPath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
  // Clarity tracks SPA navigation automatically once loaded.
}

/** Track a custom event (e.g. phone click, form submission). */
export function trackEvent(eventName, params = {}) {
  if (!isBrowser) return;
  // GA4
  if (ANALYTICS_CONFIG.GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag("event", eventName, params);
  }
  // Clarity — tag the session with the event so it appears in filters
  if (ANALYTICS_CONFIG.CLARITY_PROJECT_ID && window.clarity) {
    try {
      window.clarity("event", eventName);
      Object.entries(params).forEach(([k, v]) => {
        window.clarity("set", k, String(v));
      });
    } catch (_) {
      /* no-op */
    }
  }
}
